/* eslint-disable no-magic-numbers */
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useAppSelector } from '@/redux/hooks/useAppSelector';
import { MapModel } from '@/types/models';
import { Autocomplete } from '@/shared/Autocomplete';
import { modelsDataSelector } from '@/redux/models/models.selectors';
import { Button } from '@/shared/Button';
import {
  getModelElementsForModel,
  updateElementSubmap,
} from '@/redux/modelElements/modelElements.thunks';
import { showToast } from '@/utils/showToast';
import { closeModal } from '@/redux/modal/modal.slice';
import { SerializedError } from '@reduxjs/toolkit';
import { elementGlyphStateSelector } from '@/redux/modal/modal.selector';
import { useAppDispatch } from '@/redux/hooks/useAppDispatch';
import { LoadingIndicator } from '@/shared/LoadingIndicator';

export type ElementSubmapFormHandles = {
  clearMapModelInput: () => void;
};

type ElementSubmapFormProps = {
  selectedMapModel: number | null;
  setSelectedMapModel: (modelId: number | null) => void;
};

export const ElementSubmapForm = forwardRef<ElementSubmapFormHandles, ElementSubmapFormProps>(
  ({ selectedMapModel, setSelectedMapModel }, ref) => {
    const models: MapModel[] = useAppSelector(modelsDataSelector);
    const initialSelectedMapModel = models.find(model => model.id === selectedMapModel);

    const elementGlyphState = useAppSelector(elementGlyphStateSelector);

    const [isSending, setIsSending] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const handleMapModelChange = (mapModel: MapModel | null): void => {
      const glyphId = mapModel?.id || null;
      setSelectedMapModel(glyphId);
    };

    const handleSubmit = async (): Promise<void> => {
      if (!elementGlyphState) {
        return;
      }
      setIsSending(true);
      try {
        await dispatch(
          updateElementSubmap({
            modelId: elementGlyphState.model,
            elementId: elementGlyphState.id,
            submapId: selectedMapModel,
          }),
        ).unwrap();
        showToast({
          type: 'success',
          message: 'The element glyph has been successfully updated.',
        });
        dispatch(getModelElementsForModel(elementGlyphState.model));
        dispatch(closeModal());
      } catch (error) {
        const typedError = error as SerializedError;
        showToast({
          type: 'error',
          message: typedError.message || 'An error occurred while updating a glyph.',
        });
      } finally {
        setIsSending(false);
      }
    };

    useImperativeHandle(ref, () => ({
      clearMapModelInput: (): void => {},
    }));

    return (
      <div>
        {isSending && (
          <div className="c-element-glyph-modal-loader">
            <LoadingIndicator width={44} height={44} />
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="mb-4 flex flex-col gap-2">
            <span>Submap:</span>
            <Autocomplete<MapModel>
              options={models}
              initialValue={initialSelectedMapModel}
              clearable
              valueKey="id"
              labelKey="name"
              onChange={handleMapModelChange}
            />
          </div>
          <div />
          <div className="mb-4 flex flex-col gap-2 align-bottom">
            <Button
              type="button"
              className="mt-[28px] justify-center text-base font-medium"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    );
  },
);

ElementSubmapForm.displayName = 'ElementSubmapForm';
